import React, { useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { BottomBar } from '../BottomBar';
import {
  BudgeComponentProps,
  BudgetDollarComponent,
  BudgetSubmitReference,
  defaultOverheadCostList,
  OVERHEAD_COST_QUESTION_ID,
} from './utils';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import ProgramCostModal from './ProgramCostModal';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { HelperContainer, CostContainer } from './ProgramCost';
import RelevancyCheckbox, {
  defaultCostValue,
  isCostValid,
} from './RelevancyCheckbox';
import ShowGuidanceText from '../Review/ReportCustomQuestionType/ShowGuidanceText';

export const overheadCostCheckboxText =
  'Overhead Cost is not relevant to my program';

const StyledDiv = styled.div`
  text-align: center;
  .question-text {
    max-width: 590px;
    margin-top: 48px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .sub-heading {
    max-width: 502px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.large};
  }
  .calculate-cost {
    margin: ${SPACERS.small} auto 0;
  }
  .cost-field {
    max-width: 214px;
    margin: 0 auto;
  }
`;

const BudgetOverheadCost = React.forwardRef<
  BudgetSubmitReference,
  BudgeComponentProps & BudgetDollarComponent
>(
  (
    {
      reportId,
      initialValue,
      guidanceText,
      hideBottomBar,
      isReportPassed,
      onNext,
      onPrevious,
    },
    forwardRef,
  ) => {
    const [open, setOpen] = useState<boolean>(false);
    const [cost, setCost] = useState<string>(initialValue);
    const handleNext = () => onNext(cost);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useImperativeHandle(forwardRef, () => ({
      error: () => (cost ? undefined : 'Please enter a valid cost.'),
      handleSubmit: () => JSON.stringify({ currency: cost }),
    }));

    return (
      <>
        <StyledDiv>
          <TrueImpactText variant="heading-page" className="question-text">
            {isReportPassed
              ? 'What was the overhead cost of your program for this report year?'
              : 'What is the expected overhead cost of your program for this report year?'}
          </TrueImpactText>
          <ShowGuidanceText
            sectionId="budget"
            questionId={OVERHEAD_COST_QUESTION_ID}
            guidanceText={guidanceText}
          />
          <TrueImpactText className="sub-heading">
            This includes salaries, rent, utilities and more.
          </TrueImpactText>
          <CostContainer>
            <TrueImpactDollarField
              value={cost}
              onChange={setCost}
              disabled={cost === defaultCostValue}
              toFixedDecimal={2}
              placeholder="Total overhead cost"
              className="cost-field"
            />
            <RelevancyCheckbox
              value={cost}
              handleValue={setCost}
              text={overheadCostCheckboxText}
            />
          </CostContainer>
          <HelperContainer>
            <TrueImpactText variant="heading-subheading" align="center">
              Want some help calculating overhead cost?
            </TrueImpactText>
            <TrueImpactText variant="body" align="center">
              You don’t have to use this, but we’ve got a handy calculator just
              for this that will help.
            </TrueImpactText>
            <TrueImpactButton
              disabled={false}
              variant="secondary-large"
              onClick={handleOpen}
              className="calculate-cost"
            >
              Help me calculate this
            </TrueImpactButton>
          </HelperContainer>
          {!hideBottomBar && (
            <BottomBar
              disabled={false}
              hideBack={false}
              hideNext={!isCostValid(cost)}
              handleBack={onPrevious}
              handleNext={handleNext}
              backTitle="Back"
              nextTitle="Save & Continue"
            />
          )}
        </StyledDiv>
        <ProgramCostModal
          open={open}
          reportId={reportId}
          title="Calculate overhead cost"
          defaultCostList={defaultOverheadCostList}
          setCost={setCost}
          handleClose={handleClose}
        />
      </>
    );
  },
);

export default BudgetOverheadCost;
